/**
 * CommunityNeeds — Resource matching feed (DEV-1147)
 *
 * Replaces the former three-level Community → Project → Need accordion with a
 * flat, ranked list of collapsible "Compact" need rows (approved design:
 * design-mockups/resource-matching-spotlight-V1.html).
 *
 *   - When a resource is selected, needs are scored against it (DEV-1148
 *     resourceMatch engine) and split into "Strong matches" (auto-shown) vs
 *     "Other needs nearby" (collapsed to a count).
 *   - With no resource selected, needs are browsed by urgency (no match meter).
 *   - Each row collapses to icon + name + priority + one meta line + match
 *     meter + Offer; clicking expands it in place for lead, project/community
 *     links, dates, description, tags, and Suggest source.
 *
 * Proximity scoring uses per-need community-hierarchy hop distance supplied by
 * the parent (hopByItemGuid), derived from the tiered community-needs fetch.
 */

import React, { useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { mediaSrc } from '../../api/mediaUrl'; // single tolerant image-src resolver
import type { CommunityNeedItem } from '../../api/communityNeeds';
import type { UserResource } from '../../Data/UserResources/userResourceTypes';
import {
  rankNeedsForResource,
  hopsToTier,
  type MatchBucket,
  type ProximityTier,
} from '../../Data/UserResources/resourceMatch';
import styles from './CommunityNeeds.module.css';

// ── Props ───────────────────────────────────────────────────────

interface CommunityNeedsProps {
  needs: CommunityNeedItem[];
  /** Selected resource to score against; null = browse mode. */
  selectedResource: UserResource | null;
  /** Per-need community-hierarchy hop distance (item_guid → hops). */
  hopByItemGuid: Record<string, number>;
  searchText?: string;
  onSourceClick: (need: CommunityNeedItem) => void;
  /** Offer this resource against the need. Fully wired in DEV-1149. */
  onOffer: (need: CommunityNeedItem) => void;
  isLoading: boolean;
  /** When provided, renders a footer button to widen reach beyond the
   *  default direct-community + one-hop scope. */
  onWiden?: () => void;
  isWidening?: boolean;
}

// ── Static maps ─────────────────────────────────────────────────

function getCategoryIcon(category: string): string {
  const icons: Record<string, string> = {
    materials: '📦', equipment: '🔧', services: '🤝', labor: '👷',
    transportation: '🚌', facilities: '🏢', admin_legal: '📋', knowledge: '📚',
    financial: '💰', in_kind: '🎁', software_tech: '💻', other: '📎',
    tools_equipment: '🔧', administrative: '📋', land_site: '🏢',
  };
  return icons[category] || '📦';
}

function priorityClass(priority: string): string {
  switch (priority) {
    case 'critical': return styles.prioCritical;
    case 'important': return styles.prioImportant;
    default: return styles.prioNice;
  }
}

function bucketClass(b: MatchBucket): string {
  return b === 'strong' ? styles.matchHigh : b === 'partial' ? styles.matchMid : styles.matchLow;
}

function resolveAvatarUrl(raw: string | null | undefined): string | null {
  // Delegates to the shared tolerant resolver so every stored format works.
  return mediaSrc(raw);
}

function formatDateShort(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
}

function initialsOf(name: string | null | undefined): string {
  if (!name) return '';
  return name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
}

// ── Component ───────────────────────────────────────────────────

export default function CommunityNeeds({
  needs,
  selectedResource,
  hopByItemGuid,
  searchText = '',
  onSourceClick,
  onOffer,
  isLoading,
  onWiden,
  isWidening = false,
}: CommunityNeedsProps) {
  const { t } = useAppTranslation('userProfile');
  const navigate = useNavigate();

  const widenFooter = onWiden ? (
    <button type="button" className={styles.widenBtn} onClick={onWiden} disabled={isWidening}>
      {isWidening ? t('resources.loading') : t('resources.showWiderCommunities')}
    </button>
  ) : null;

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [otherOpen, setOtherOpen] = useState(false);

  const toggleNeed = useCallback((guid: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(guid)) next.delete(guid);
      else next.add(guid);
      return next;
    });
  }, []);

  const bucketLabel = useCallback(
    (b: MatchBucket) =>
      b === 'strong' ? t('resources.matchStrong')
        : b === 'partial' ? t('resources.matchPartial')
          : t('resources.matchLow'),
    [t]
  );

  const tierLabel = useCallback(
    (tier: ProximityTier | null): string => {
      switch (tier) {
        case 'town': return t('resources.tierTown');
        case 'county': return t('resources.tierCounty');
        case 'region': return t('resources.tierRegion');
        default: return '';
      }
    },
    [t]
  );

  // Text-search filter (word-level, case-insensitive).
  const visible = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return needs;
    const words = q.split(/\s+/).filter(Boolean);
    return needs.filter((n) => {
      const hay = `${n.label || ''} ${n.description || ''} ${(n.descriptor_tags || []).join(' ')}`.toLowerCase();
      return words.some((w) => hay.includes(w));
    });
  }, [needs, searchText]);

  // Rank against the selected resource (or null → browse order).
  const { strong, other, browse } = useMemo(() => {
    if (!selectedResource) {
      const order: Record<string, number> = { critical: 0, important: 1, nice_to_have: 2 };
      const sorted = [...visible].sort((a, b) =>
        (order[a.priority] ?? 3) - (order[b.priority] ?? 3) ||
        ((hopByItemGuid[a.item_guid] ?? 99) - (hopByItemGuid[b.item_guid] ?? 99))
      );
      return { strong: [], other: [], browse: sorted };
    }
    const ranked = rankNeedsForResource(selectedResource, visible, {
      hopsFor: (n) => hopByItemGuid[n.item_guid],
    });
    return {
      strong: ranked.filter((r) => r.score.bucket === 'strong'),
      other: ranked.filter((r) => r.score.bucket !== 'strong'),
      browse: [],
    };
  }, [selectedResource, visible, hopByItemGuid]);

  // ── Need row (collapsed Compact + expand-in-place) ──
  const renderRow = useCallback(
    (need: CommunityNeedItem, score: { total: number; bucket: MatchBucket } | null) => {
      const open = expanded.has(need.item_guid);
      const hops = hopByItemGuid[need.item_guid];
      const tier = hopsToTier(hops);
      const gap = need.target_quantity != null ? Math.max(0, need.target_quantity - need.current_quantity) : null;
      const gapTxt = gap != null
        ? `${gap} ${need.unit || ''} ${t('resources.stillNeeded')}`.replace(/\s+/g, ' ').trim()
        : (need.priority === 'critical' ? `${t('resources.oneNeeded')} · ${t('resources.urgentTag')}` : t('resources.oneNeeded'));
      const avatarUrl = resolveAvatarUrl(need.project_owner_avatar_url);
      const tierTxt = tierLabel(tier);

      const projLink = (
        <button type="button" className={styles.lnk}
          onClick={(e) => { e.stopPropagation(); navigate(`/projects/${need.project_guid}`); }}>
          {need.project_name}
        </button>
      );
      const communityLink = (
        <button type="button" className={styles.lnk}
          onClick={(e) => { e.stopPropagation(); navigate(`/communities/${need.community_entity_guid}`); }}>
          {need.community_name || t('resources.unknownCommunity')}
        </button>
      );

      return (
        <div
          key={need.item_guid}
          className={`${styles.needRow} ${score?.bucket === 'strong' ? styles.strong : ''} ${open ? styles.open : ''}`}
          onClick={() => toggleNeed(need.item_guid)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleNeed(need.item_guid); } }}
        >
          <div className={styles.rowMain}>
            <span className={styles.nIcon}>{getCategoryIcon(need.category)}</span>
            <div className={styles.nBody}>
              <div className={styles.nName}>
                <span className={`${styles.prio} ${priorityClass(need.priority)}`} />
                <span className={styles.nNameText}>{need.label}</span>
              </div>
              <div className={styles.nSub}>
                <span>{need.project_name}</span>
                <span className={styles.sep}>·</span>
                <span>{need.community_name || t('resources.unknownCommunity')}</span>
                {tierTxt && <span className={styles.tierBadge}>{tierTxt}</span>}
                <span className={styles.gapChip}>{gapTxt}</span>
              </div>
            </div>
            <div className={styles.nRight}>
              {score && (
                <div className={styles.miniMatch} title={`${bucketLabel(score.bucket)} · ${score.total}/100`}>
                  <div className={`${styles.matchLabel} ${bucketClass(score.bucket)}`}>{bucketLabel(score.bucket)}</div>
                  <div className={styles.meter}><span className={bucketClass(score.bucket)} style={{ width: `${score.total}%` }} /></div>
                </div>
              )}
              <button type="button" className={styles.offerBtn}
                onClick={(e) => { e.stopPropagation(); onOffer(need); }}>
                {t('resources.offerResource')}
              </button>
              <span className={`${styles.chev} ${open ? styles.chevOpen : ''}`}>▸</span>
            </div>
          </div>

          {open && (
            <div className={styles.detail}>
              <div className={styles.detailRow}>
                <div className={styles.detailLabel}>{t('resources.needs')}</div>
                <div className={styles.detailLine}>
                  {need.target_quantity != null && need.target_quantity > 1
                    ? `${need.current_quantity}/${need.target_quantity} ${need.unit || ''} · ${gapTxt}`
                    : gapTxt}
                </div>
              </div>

              {need.project_owner_name && (
                <button type="button" className={styles.leadRow}
                  onClick={(e) => { e.stopPropagation(); if (need.project_owner_guid) navigate(`/profile/${need.project_owner_guid}`); }}>
                  {avatarUrl
                    ? <img src={avatarUrl} alt="" className={styles.leadAvatar} />
                    : <span className={styles.leadAvatarFallback}>{initialsOf(need.project_owner_name)}</span>}
                  <span className={styles.leadName}>{need.project_owner_name}</span>
                  <span className={styles.leadLabel}>{t('resources.lead')}</span>
                </button>
              )}

              <div className={styles.detailRow}>
                <div className={styles.detailLabel}>{t('resources.projectLabel')}</div>
                <div className={styles.detailLine}>
                  {projLink}
                  {(need.project_start_date || need.project_finish_date) && (
                    <span className={styles.dates}>
                      {' · '}
                      {[formatDateShort(need.project_start_date), formatDateShort(need.project_finish_date)].filter(Boolean).join(' – ')}
                    </span>
                  )}
                </div>
              </div>

              <div className={styles.detailRow}>
                <div className={styles.detailLabel}>{t('resources.communityLabel')}</div>
                <div className={styles.detailLine}>{communityLink}{tierTxt && <span className={styles.tierBadge}>{tierTxt}</span>}</div>
              </div>

              {need.description && (
                <div className={styles.detailRow}>
                  <div className={styles.detailLabel}>{t('resources.description')}</div>
                  <div className={styles.detailLine}>{need.description}</div>
                </div>
              )}

              {need.descriptor_tags.length > 0 && (
                <div className={styles.tags}>
                  {need.descriptor_tags.map((tag) => <span key={tag} className={styles.tag}>{tag}</span>)}
                </div>
              )}

              <div className={styles.detailActions}>
                <button type="button" className={styles.linkBtn}
                  onClick={(e) => { e.stopPropagation(); navigate(`/projects/${need.project_guid}`); }}>
                  {t('resources.viewProject')} →
                </button>
                {need.project_owner_guid && (
                  <button type="button" className={styles.linkBtn}
                    onClick={(e) => { e.stopPropagation(); navigate(`/profile/${need.project_owner_guid}`); }}>
                    {t('resources.viewProfile')} →
                  </button>
                )}
                <button type="button" className={styles.srcBtn}
                  onClick={(e) => { e.stopPropagation(); onSourceClick(need); }}>
                  {t('resources.sourceAction')}
                  {need.source_count > 0 ? ` · ${need.source_count}` : ''}
                </button>
              </div>
            </div>
          )}
        </div>
      );
    },
    [expanded, hopByItemGuid, navigate, onOffer, onSourceClick, toggleNeed, bucketLabel, tierLabel, t]
  );

  // ── Loading / empty ──
  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}><div className={styles.loadingSpinner} /><p>{t('resources.loading')}</p></div>
      </div>
    );
  }
  if (needs.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <p className={styles.emptyMessage}>{t('resources.noCommunityNeeds')}</p>
          <p className={styles.emptyHint}>{t('resources.broaderSearch')}</p>
        </div>
        {widenFooter}
      </div>
    );
  }

  // ── Browse mode (no resource selected) ──
  if (!selectedResource) {
    return (
      <div className={styles.container}>
        {browse.length > 0
          ? browse.map((n) => renderRow(n, null))
          : <div className={styles.emptyState}><p className={styles.emptyMessage}>{t('resources.noMatchingNeeds')}</p></div>}
        {widenFooter}
      </div>
    );
  }

  // ── Spotlight mode (resource selected) ──
  return (
    <div className={styles.container}>
      <div className={styles.sectionLabel}>
        {t('resources.matchesYourResource')} <span className={styles.sectionCount}>{strong.length}</span>
        <span className={styles.sectionLine} />
      </div>
      {strong.length > 0
        ? strong.map((r) => renderRow(r.need, r.score))
        : <div className={styles.emptyState}><p className={styles.emptyHint}>{t('resources.noMatchingNeeds')}</p></div>}

      {other.length > 0 && (
        <>
          <button type="button" className={styles.sectionToggle} onClick={() => setOtherOpen((v) => !v)}>
            <span className={`${styles.chev} ${otherOpen ? styles.chevOpen : ''}`}>▸</span>
            {t('resources.otherCommunityNeeds')} <span className={styles.sectionCount}>{other.length}</span>
            <span className={styles.sectionLine} />
          </button>
          {otherOpen && other.map((r) => renderRow(r.need, r.score))}
        </>
      )}
      {widenFooter}
    </div>
  );
}
